import { normalizeChartingAlerts, normalizeChartingRegistry } from '@/features/charting-next/adapters/chartingActivityAdapters';
import { normalizeChartingMutationResult } from '@/features/charting-next/adapters/chartingPreferenceAdapters';
import { get, postForm } from '@/features/charting-next/api/chartingRepositoryClient';

const isNotFoundError = (error) => Number(error?.cause?.response?.status || error?.response?.status) === 404;

const fetchTickerScopedAlertsPayload = async ({ uid, ticker }) => {
    try {
        return await get(`/api/v1/eurekers-alerts/user/${encodeURIComponent(uid)}/ticker/${encodeURIComponent(ticker)}/get`);
    } catch (error) {
        if (!isNotFoundError(error)) {
            throw error;
        }

        return postForm('/api/v1/eurekers-alerts/get-user-alerts', {
            uid
        });
    }
};

export async function fetchChartingTickerAlerts({ uid = '', ticker = '' } = {}) {
    const normalizedUid = String(uid || '').trim();
    const normalizedTicker = String(ticker || '')
        .trim()
        .toUpperCase();

    if (!normalizedUid || !normalizedTicker) {
        return [];
    }

    const payload = await fetchTickerScopedAlertsPayload({
        uid: normalizedUid,
        ticker: normalizedTicker
    });

    return normalizeChartingAlerts(payload, normalizedTicker);
}

export async function createChartingTickerAlert({ uid = '', ticker = '', field = '', mode = '', value = '' } = {}) {
    const payload = await postForm('/api/v1/eurekers-alerts/create', {
        uid,
        ticker,
        alert_field: field,
        alert_mode: mode,
        alert_data: value
    });

    return normalizeChartingMutationResult(payload, 'La alarma se ha creado.');
}

export async function deleteChartingTickerAlert({ uid = '', alertId = '' } = {}) {
    const payload = await postForm('/api/v1/eurekers-alerts/delete', {
        uid,
        alert_id: alertId
    });

    return normalizeChartingMutationResult(payload, 'La alarma se ha eliminado.');
}

export async function fetchChartingTickerRegistry({ uid = '', ticker = '' } = {}) {
    const normalizedUid = String(uid || '').trim();
    const normalizedTicker = String(ticker || '')
        .trim()
        .toUpperCase();

    if (!normalizedUid || !normalizedTicker) {
        return [];
    }

    const [registry, notes, alerts] = await Promise.all([
        get(`/api/v1/eurekers-historical-registry/get-historical-registry-by-user/user/${encodeURIComponent(normalizedUid)}`),
        get(`/api/v1/eurekers-notes/user/${encodeURIComponent(normalizedUid)}/get`),
        postForm('/api/v1/eurekers-alerts/get-user-alerts', {
            uid: normalizedUid
        })
    ]);

    return normalizeChartingRegistry(
        {
            registry,
            notes,
            alerts
        },
        normalizedTicker
    );
}

export async function createChartingTickerNote({ uid = '', ticker = '', body = '' } = {}) {
    const payload = await postForm(
        '/api/v1/eurekers-notes/create',
        {
            uid,
            ticker,
            title: '',
            body: String(body || '').trim()
        },
        {
            formOptions: {
                skipEmptyString: false
            }
        }
    );

    return normalizeChartingMutationResult(payload, 'La nota se ha guardado.');
}

export async function deleteChartingTickerNote({ uid = '', noteId = '' } = {}) {
    const payload = await postForm('/api/v1/eurekers-notes/delete', {
        uid,
        nid: noteId
    });

    return normalizeChartingMutationResult(payload, 'La nota se ha eliminado.');
}

export async function reportChartingTickerError({ ticker = '', type = '', message = '' } = {}) {
    const payload = await postForm('/api/v1/stock-finder/report-error', {
        ticker,
        type,
        message
    });

    return normalizeChartingMutationResult(payload, 'El error se ha enviado.');
}
